import {
  createElement,
  type ElementTypes,
  type Payouts,
} from '@airwallex/components-sdk'

import { type PaymentMethodCategoryType } from '@webapp/platform/banking/entities/enums/PaymentMethodCategoryEnum'
import { PAYMENT_METHOD_CATEGORY_CONFIGS } from './constants'
import { BankingUseCaseErrors } from '../BankingUseCaseErrors'
import { AIRWALLEX_COMPONENTS_THEME } from '../constants'

export class CreateBeneficiaryUseCase {
  constructor(private deps: {}) {}

  execute = async ({
    mountElementId,
    paymentMethodCategory,
    onReady,
    onChange,
    onFormState,
    onError,
  }: {
    mountElementId: string
    paymentMethodCategory: Exclude<
      PaymentMethodCategoryType,
      'EXTERNAL' | 'INTERNATIONAL_US'
    >
    onReady?: () => void
    onChange?: (event: Payouts.BeneficiaryFormElementEvent['change']) => void
    onFormState?: (
      state: Payouts.BeneficiaryFormElementEvent['formState']
    ) => void
    onError: (error: Error) => void
  }): Promise<ElementTypes['beneficiaryForm'] | null> => {
    if (!mountElementId) {
      console.error('Mount element id is required')
      return null
    }

    const config = PAYMENT_METHOD_CATEGORY_CONFIGS[paymentMethodCategory]

    if (!config) {
      console.error(
        `No config found for payment method category: ${paymentMethodCategory}`
      )
      return null
    }

    try {
      const beneficiaryElement = await createElement('beneficiaryForm', {
        apiVersion: '2025-09-30',
        theme: {
          ...AIRWALLEX_COMPONENTS_THEME,
          palette: {
            ...AIRWALLEX_COMPONENTS_THEME.palette,
            primary: {
              ...AIRWALLEX_COMPONENTS_THEME.palette.primary,
              '10': '#F2F2F2', // hover color
              '30': '#F2F2F2', // active color
              '70': '#3078A0', // background of button
            },
            gradients: {
              secondary: ['#fff', '#fff'], // transfer method side bar color
            },
          },
        },
        defaultValues: config.defaultValues,
        customizations: config.customizations,
      })

      const containerElement = document.getElementById(mountElementId)

      if (containerElement) {
        beneficiaryElement.mount(containerElement)
      } else {
        throw new BankingUseCaseErrors({
          name: 'MOUNT_ELEMENT_NOT_FOUND',
          message: `Mount element with id "${mountElementId}" not found`,
        })
      }

      beneficiaryElement.on('ready', () => {
        if (onReady) onReady()
      })

      beneficiaryElement.on('change', (event) => {
        if (onChange)
          onChange(event as Payouts.BeneficiaryFormElementEvent['change'])
      })

      beneficiaryElement.on('formState', (state) => {
        if (onFormState)
          onFormState(state as Payouts.BeneficiaryFormElementEvent['formState'])
      })

      beneficiaryElement.on('error', (error) => {
        if (error && 'errors' in error) {
          onError(new Error(error.errors))
        } else if (error && 'code' in error) {
          onError(new Error(error.code))
        }
      })

      return beneficiaryElement
    } catch (error) {
      throw new BankingUseCaseErrors({
        name: 'ERROR_RENDERING_AIRWALLEX_BENEFICIARY_FORM',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to render Airwallex beneficiary form',
      })
    }
  }
}
